import React from "react";
import { useNavigate } from "react-router-dom";

interface AnimeCharacter {
  id: string;
  name: string;
  image: string;
  series?: string;
}

interface ExampleVideo {
  id: string;
  promptImage: string;
  resultVideo: string;
  prompt: string;
  duration: string;
}

// Anime characters from public/baddies/
const animeCharacters: AnimeCharacter[] = [
  {
    id: "anime1",
    name: "Megumin",
    image: "/baddies/anime1.jpg",
    series: "KonoSuba",
  },
  {
    id: "anime2",
    name: "Megumin",
    image: "/baddies/anime2.jpg",
    series: "KonoSuba",
  },
  {
    id: "anime3",
    name: "Mitsuri",
    image: "/baddies/anime3.jpg",
    series: "Demon Slayer",
  },
  {
    id: "anime4",
    name: "Power",
    image: "/baddies/anime4.jpg",
    series: "Chainsaw Man",
  },
];

// Example videos for "Try these" section
// Using anime images that correspond to the example videos
const exampleVideos: ExampleVideo[] = [
  {
    id: "example1",
    promptImage: "/baddies/anime1.jpg",
    resultVideo: "https://funnyclips-content.s3.us-east-1.amazonaws.com/example17.mp4",
    prompt: "Sensual dancing in tight outfit, close up body movements, seductive poses",
    duration: "00:05",
  },
  {
    id: "example2",
    promptImage: "/baddies/anime3.jpg",
    resultVideo: "https://funnyclips-content.s3.us-east-1.amazonaws.com/example18.mp4",
    prompt: "Stripping clothes slowly, revealing lingerie, teasing movements",
    duration: "00:05",
  },
  {
    id: "example3",
    promptImage: "/baddies/anime4.jpg",
    resultVideo: "https://funnyclips-content.s3.us-east-1.amazonaws.com/example19.mp4",
    prompt: "Provocative poses in revealing dress, sultry expressions, intimate camera angles",
    duration: "00:05",
  },
];

const HNoFilter: React.FC = () => {
  const navigate = useNavigate();

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        // Analytics removed for showcase site

        // Navigate directly to creation with uploaded image
        navigate("/video/i2v", {
          state: {
            referenceImage: result,
            selectedModel: "wan2.2-nofilter",
            noFilterMode: true,
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCharacterClick = (character: AnimeCharacter) => {
    // Analytics removed for showcase site

    // Navigate to image-to-video with character
    navigate("/video/i2v", {
      state: {
        referenceImage: character.image,
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  const handleExampleTryClick = (example: ExampleVideo) => {
    // Analytics removed for showcase site

    // Navigate to image-to-video with pre-filled data
    navigate("/video/i2v", {
      state: {
        referenceImage: example.promptImage,
        prefilledPrompt: example.prompt,
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  const handleCreateClick = () => {
    // Analytics removed for showcase site

    // Navigate to image-to-video
    navigate("/video/i2v", {
      state: {
        selectedModel: "wan2.2-nofilter",
        noFilterMode: true,
      },
    });
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Hero Upload Section */}
      <div className="relative min-h-[70vh] flex items-center justify-center px-4 py-12 pb-0 md:pb-2">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 opacity-50" />
        <div
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{ backgroundImage: "url(/bg.png)" }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center space-y-6">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
            </span>
            <span className="text-white text-sm font-semibold">ANIME • HENTAI • WAIFU</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight">
            Upload Any Waifu.
            <br />
            Make Her <span className="text-white">Move For You</span> 🌶️
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-gray-300 max-w-2xl mx-auto">
            Turn any anime girl into an uncensored AI video in 60 seconds
          </p>

          {/* Large Upload Box */}
          <div className="max-w-2xl mx-auto pt-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              id="hero-upload"
              className="hidden"
            />
            <label
              htmlFor="hero-upload"
              className="group relative block cursor-pointer"
            >
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 border-2 border-dashed border-white/30 hover:border-white/60 rounded-3xl p-10 md:p-12 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_50px_rgba(255,255,255,0.15)]">
                {/* Upload Icon */}
                <div className="mb-4">
                  <div className="w-16 h-16 md:w-20 md:h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                    <svg
                      className="w-8 h-8 md:w-10 md:h-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                  </div>
                </div>

                {/* Upload Text */}
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                    Drop Your Waifu Here
                  </h3>
                  <p className="text-gray-400 text-base md:text-lg">
                    Or click to browse • PNG, JPG up to 10MB
                  </p>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 rounded-3xl transition-colors pointer-events-none" />
              </div>
            </label>
          </div>

          {/* Quick Stats Under Upload */}
          <div className="flex items-center justify-center gap-6 md:gap-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Instant Upload</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>60 Sec Results</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>No Watermark</span>
            </div>
          </div>
        </div>
      </div>

      {/* Try These Examples Section */}
      <div className="relative px-4 pt-0 md:pt-2 pb-8 bg-black">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
            New user? Try these:
          </h2>

          <div className="space-y-4">
            {exampleVideos.map((example, index) => (
              <div
                key={example.id}
                className="relative bg-gray-900/50 rounded-2xl p-3 sm:p-4 border border-white/10"
              >
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  {/* Prompt Image */}
                  <div className="relative aspect-square">
                    <div className="absolute top-2 left-2 bg-black/80 px-2 py-1 rounded-lg z-10">
                      <span className="text-[10px] sm:text-xs font-semibold text-white">Prompt</span>
                    </div>
                    <img
                      src={example.promptImage}
                      alt="Prompt"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>

                  {/* Result Video */}
                  <div className="relative aspect-square">
                    <div className="absolute top-2 left-2 bg-black/80 px-2 py-1 rounded-lg z-10">
                      <span className="text-[10px] sm:text-xs font-semibold text-white">Result</span>
                    </div>
                    <div className="absolute top-2 right-2 bg-black/80 px-2 py-1 rounded z-10">
                      <span className="text-[10px] sm:text-xs font-medium text-white">{example.duration}</span>
                    </div>
                    <video
                      src={example.resultVideo}
                      loop
                      muted
                      playsInline
                      autoPlay
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                </div>
                
                {/* Try Button - Centered Between Both */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
                  <button
                    onClick={() => handleExampleTryClick(example)}
                    className="bg-white text-black px-5 sm:px-6 py-2 sm:py-2.5 rounded-full font-semibold text-xs sm:text-sm hover:bg-gray-200 hover:scale-105 transition-all shadow-lg"
                  >
                    Try
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Anime Characters */}
      <div className="relative px-4 py-12 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2 mb-4">
              <span className="text-white text-sm font-semibold">POPULAR WAIFUS</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-3">
              Or Pick a Waifu
            </h2>
            <p className="text-gray-400 text-lg">
              Start with one of these anime girls
            </p>
          </div>

          {/* Large Character Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {animeCharacters.map((character, index) => (
              <button
                key={character.id}
                onClick={() => handleCharacterClick(character)}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden border-2 border-white/20 hover:border-white/60 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img
                  src={character.image}
                  alt={character.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
                
                {/* Character Info */}
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                  {character.series && (
                    <p className="text-gray-400 text-xs font-semibold mb-1">
                      {character.series}
                    </p>
                  )}
                  <h3 className="text-white font-bold text-lg md:text-xl drop-shadow-lg mb-2">
                    {character.name}
                  </h3>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 rounded-full text-sm font-bold">
                      <span>Create Now</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Upload Your Own Waifu */}
          <div className="text-center">
            <p className="text-gray-400 text-lg mb-4">
              Can't find your waifu? Upload your own anime character
            </p>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-3 bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/50 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Upload Your Waifu
            </button>
          </div>
        </div>
      </div>

      {/* How It Works - Anime Style */}
      <div className="relative px-4 py-12 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              How It Works
            </h2>
            <p className="text-gray-400 text-lg">
              Simple, fast, and completely uncensored
            </p>
          </div>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="relative bg-white/5 border-l-4 border-white/40 rounded-r-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Pick Your Waifu or Upload
                  </h3>
                  <p className="text-gray-400">
                    Choose from our curated anime characters or upload any anime/hentai image
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative bg-white/5 border-l-4 border-white/40 rounded-r-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Describe Your Fantasy
                  </h3>
                  <p className="text-gray-400">
                    Type exactly what you want to happen - no limits, no censorship
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative bg-white/5 border-l-4 border-white/40 rounded-r-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white text-black rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Get Your Uncensored Video
                  </h3>
                  <p className="text-gray-400">
                    Download your creation in 60 seconds. Full resolution, no watermarks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example Preview Section */}
      <div className="relative px-4 py-12 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Example Creations
            </h2>
            <p className="text-gray-400">
              See what the AI can do with anime characters
            </p>
          </div>

          {/* Video Examples - Horizontal Scroll */}
          <div className="flex gap-4 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory mb-8">
            <div className="group relative flex-shrink-0 w-64 aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 snap-start hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example17.mp4"
                poster="/baddies/anime1.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-gray-400 text-xs font-semibold mb-1">Megumin</p>
                <h3 className="text-white font-bold text-base drop-shadow-lg">
                  Explosion Magic
                </h3>
              </div>
            </div>

            <div className="group relative flex-shrink-0 w-64 aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 snap-start hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example18.mp4"
                poster="/baddies/anime3.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-gray-400 text-xs font-semibold mb-1">Mitsuri</p>
                <h3 className="text-white font-bold text-base drop-shadow-lg">
                  Love Breathing
                </h3>
              </div>
            </div>

            <div className="group relative flex-shrink-0 w-64 aspect-[3/4] rounded-xl overflow-hidden border-2 border-white/20 hover:border-white/40 transition-all duration-300 snap-start hover:shadow-[0_0_20px_rgba(255,255,255,0.1)]">
              <video
                src="https://funnyclips-content.s3.us-east-1.amazonaws.com/example19.mp4"
                poster="/baddies/anime4.jpg"
                loop
                muted
                playsInline
                autoPlay
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <p className="text-gray-400 text-xs font-semibold mb-1">Power</p>
                <h3 className="text-white font-bold text-base drop-shadow-lg">
                  Blood Manipulation
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="relative px-4 py-16 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-4xl md:text-6xl font-black text-white leading-tight">
            Bring Your Waifu to Life
          </h2>
          <p className="text-xl text-gray-300">
            Create uncensored anime AI videos in seconds
          </p>
          <button
            onClick={handleCreateClick}
            className="group inline-flex items-center gap-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xl font-bold px-12 py-5 rounded-full transition-all duration-300 shadow-[0_0_40px_rgba(219,39,119,0.4)] hover:shadow-[0_0_60px_rgba(219,39,119,0.6)] hover:scale-110"
          >
            Get Started
            <svg
              className="w-6 h-6 group-hover:translate-x-2 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Age Verification & Disclaimer */}
      <div className="relative px-4 py-8 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-2">
            <span className="text-red-400 text-lg font-bold">18+</span>
            <span className="text-red-300 text-sm font-semibold">Adults Only</span>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed">
            The "No Filter" mode is designed for creative and entertainment purposes for adults. 
            Users must be 18+ and are responsible for ensuring their content complies with platform 
            guidelines and applicable laws. PixelMaker does not endorse or take responsibility for 
            user-generated content. All characters depicted are fictional.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HNoFilter;

